import { useEffect, useMemo, useState } from "react";
import { Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { Email, FilterListAlt } from "@mui/icons-material";
import { translate } from "../../../../../translate/translate";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { ErrorComponent } from "../../../../../components/error/error.component";
import { NpiChecklistItem, NpiChecklistItemStatus } from "../../../../../models/npi/npi-checklist";
import type { ChecklistSection } from "../types/npi-checklist.types";
import { buildChecklistSections, getChecklistOwners } from "../utils/checklist.utils";
import { CHECKLIST_ITEM_STATUS_STYLES } from "../constants/dashboard.constants";
import { cardSx, filterChip, outlinedFieldHover } from "../styles/npi-dashboard.styles";
import NpiChecklistTableComponent from "./npi-checklist-table.component";

const ALL = "all";

type StatusFilter = NpiChecklistItemStatus | typeof ALL;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: ALL, label: "checklist_all_status" },
  ...Object.values(NpiChecklistItemStatus).map((status) => ({ value: status, label: CHECKLIST_ITEM_STATUS_STYLES[status].label })),
];

interface NpiChecklistScheduleProps {
  checklistId: number;
}

const filterSections = (sections: ChecklistSection[], status: StatusFilter, owner: string): ChecklistSection[] =>
  sections.map((section) => ({
    ...section,
    subsections: section.subsections.map((subsection) => ({
      ...subsection,
      items: subsection.items.filter(
        (item) => (status === ALL || item.status === status) && (owner === ALL || item.owner === owner),
      ),
    })),
  }));

const NpiChecklistScheduleComponent = ({ checklistId }: NpiChecklistScheduleProps) => {
  const [items, setItems] = useState<NpiChecklistItem[] | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>(ALL);
  const [selectedResponsible, setSelectedResponsible] = useState<string>(ALL);

  useEffect(() => {
    setItems(null);
    const checklistService = new BaseService<NpiChecklistItem[]>(URLS.NPI_CHECKLIST);
    checklistService
      .getFromDetailRoute(checklistId, "items")
      .then((response) => setItems(response.data))
      .catch((error: any) => ErrorComponent(error));
  }, [checklistId]);

  const sections = useMemo(() => buildChecklistSections(items ?? []), [items]);
  const responsibles = useMemo(() => getChecklistOwners(items ?? []), [items]);

  const filteredSections = useMemo(
    () => filterSections(sections, selectedStatus, selectedResponsible),
    [sections, selectedStatus, selectedResponsible],
  );

  if (!items) {
    return null;
  }

  const handleClearFilters = () => {
    setSelectedStatus(ALL);
    setSelectedResponsible(ALL);
  };

  return (
    <Paper variant="outlined" sx={{ ...cardSx, p: 0, mb: 2, overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={1} useFlexGap sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1 }}>
          <FilterListAlt sx={{ color: "#0000008A" }} />
          <Typography sx={{ fontSize: "12px", fontWeight: "bold", color: "#83868C" }}>
            {translate.t("checklist_filters").toUpperCase()}:
          </Typography>
        </Stack>

        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            size="small"
            label={translate.t(filter.label)}
            onClick={() => setSelectedStatus(filter.value)}
            sx={filterChip(filter.value === selectedStatus)}
          />
        ))}

        <FormControl size="small" sx={{ minWidth: 120, ml: 1, ...outlinedFieldHover("4px") }}>
          <InputLabel id="checklist-responsible-label">{translate.t("checklist_responsibles")}</InputLabel>
          <Select
            labelId="checklist-responsible-label"
            label={translate.t("checklist_responsibles")}
            sx={{ fontSize: "14px" }}
            value={selectedResponsible}
            onChange={(event) => setSelectedResponsible(event.target.value)}>
            <MenuItem value={ALL}>{translate.t("all")}</MenuItem>
            {responsibles.map((responsible) => (
              <MenuItem key={responsible} value={responsible}>
                {responsible}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" alignItems="center" spacing={2} sx={{ ml: "auto" }}>
          <Button onClick={handleClearFilters} sx={{ fontWeight: 600, color: "#003FFB" }}>
            {translate.t("checklist_clear_filters").toUpperCase()}
          </Button>
          <Button variant="outlined" startIcon={<Email />} sx={{ fontWeight: 600, color: "#003FFB", borderColor: "#003FFB" }}>
            {translate.t("checklist_send_report").toUpperCase()}
          </Button>
        </Stack>
      </Stack>

      <NpiChecklistTableComponent key={checklistId} sections={filteredSections} />
    </Paper>
  );
};

export default NpiChecklistScheduleComponent;